import React, { useEffect, useState } from 'react';
import api from '../../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { isMobileContainer } from '../../../utils/device';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell,
} from 'recharts';
import { Ticket, Clock, AlertTriangle } from 'lucide-react';

const PIE_COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#06b6d4', '#ec4899'];

function StatCard({ title, value, icon: Icon, accent, onClick }) {
  return (
    <Card
      className={`border-border ${onClick ? 'cursor-pointer hover:bg-muted/40 transition-colors' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded-lg ${accent}`}><Icon className="h-5 w-5" /></div>
        <div>
          <p className="text-xs text-muted-foreground">{title}</p>
          <p className="text-2xl font-semibold">{value ?? 0}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function CrmTicketDashboard({ relatedEmployeeId = '', onChipClick }) {
  const [data, setData] = useState(null);
  const chartHeight = isMobileContainer() ? 'h-48' : 'h-64';

  useEffect(() => {
    const params = {};
    if (relatedEmployeeId) params.related_employee_id = relatedEmployeeId;
    api.get('/crm/tickets/dashboard', { params }).then((r) => setData(r.data?.data)).catch(() => setData(null));
  }, [relatedEmployeeId]);

  const stats = data?.stats || {};
  const charts = data?.charts || {};
  const chip = (filter) => (onChipClick ? () => onChipClick(filter) : undefined);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard title="Total" value={stats.total} icon={Ticket} accent="bg-blue-100 text-blue-700" onClick={chip({})} />
        <StatCard title="Open" value={stats.open} icon={Ticket} accent="bg-sky-100 text-sky-700" onClick={chip({ status: 'open' })} />
        <StatCard title="In Progress" value={stats.in_progress} icon={Clock} accent="bg-amber-100 text-amber-700" onClick={chip({ status: 'in_progress' })} />
        <StatCard title="Completed" value={stats.completed} icon={Ticket} accent="bg-green-100 text-green-700" onClick={chip({ status: 'completed' })} />
        <StatCard title="Overdue" value={stats.overdue} icon={AlertTriangle} accent="bg-red-100 text-red-700" onClick={chip({ overdue: true })} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {['assigned', 'waiting_for_customer', 'testing', 'closed'].map((key) => (
          <Card
            key={key}
            className={`border-border ${onChipClick ? 'cursor-pointer hover:bg-muted/40 transition-colors' : ''}`}
            onClick={chip({ status: key })}
            role={onChipClick ? 'button' : undefined}
            tabIndex={onChipClick ? 0 : undefined}
            onKeyDown={onChipClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChipClick({ status: key }); } } : undefined}
          >
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
              <p className="text-xl font-semibold">{stats[key] ?? 0}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {charts.avg_resolution_hours != null && (
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Average Resolution Time</p>
            <p className="text-2xl font-semibold">{charts.avg_resolution_hours} hours</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-border">
          <CardHeader><CardTitle className="text-base">Tickets by Priority</CardTitle></CardHeader>
          <CardContent className={chartHeight}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={charts.by_priority || []} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                  {(charts.by_priority || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader><CardTitle className="text-base">Tickets by Category</CardTitle></CardHeader>
          <CardContent className={chartHeight}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.by_category || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#8b5cf6" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader><CardTitle className="text-base">Tickets by Employee</CardTitle></CardHeader>
          <CardContent className={chartHeight}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.by_employee || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader><CardTitle className="text-base">Tickets by School</CardTitle></CardHeader>
          <CardContent className={chartHeight}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.by_school || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 8 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-border lg:col-span-2">
          <CardHeader><CardTitle className="text-base">Monthly Ticket Trends</CardTitle></CardHeader>
          <CardContent className={chartHeight}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={charts.monthly_trends || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
